import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { SplitText } from 'gsap/SplitText';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { Observer } from 'gsap/Observer';
import { $, $$, activate } from './dom.js';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, ScrollSmoother, SplitText, DrawSVGPlugin, Observer);

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const MOTION = '(prefers-reduced-motion: no-preference)';
const DESKTOP = `(min-width: 901px) and ${MOTION} and (hover: hover)`;
const INK = '#121110';

let smoother = null;

const currentScroll = () => (smoother ? smoother.scrollTop() : window.scrollY);
const quickXY = (target, vars) => ['x', 'y'].map((prop) => gsap.quickTo(target, prop, vars));

function tweenScroll(y, vars, autoKill = false) {
  return smoother
    ? gsap.to(smoother, { scrollTop: y, ...vars })
    : gsap.to(window, { scrollTo: { y, autoKill }, ...vars });
}

function scrollToTarget(target) {
  const y = smoother ? smoother.offset(target, 'top top') : target;
  tweenScroll(y, { duration: 1.1, ease: 'power3.inOut', overwrite: 'auto' }, true);
}

const TAPS = 2;
const TAP_GAP = 120;
const TAP_KEEP = 1600;
const EDGE = .002;

function stepper() {
  let taps = 0;
  let way = 0;
  let last = -Infinity;
  let settled = 0;

  const wheel = (e) => {
    const dir = Math.sign(e.deltaY);
    if (!dir) return;
    const now = e.timeStamp;
    const gap = now - last;
    const notch = e.deltaMode === 1 || Math.abs(e.deltaY) >= 50;
    if (dir !== way || gap > TAP_KEEP) taps = 0;
    if (dir !== way || gap > TAP_GAP || (notch && gap > 35)) taps += 1;
    way = dir;
    last = now;
  };
  window.addEventListener('wheel', wheel, { passive: true });

  const snapTo = (value, self) => {
    const tl = self.animation;
    const points = [...new Set([0, 1, ...Object.values(tl.labels).map((t) => t / tl.duration())])]
      .map((p) => gsap.utils.clamp(EDGE, 1 - EDGE, p))
      .sort((a, b) => a - b);
    const closest = gsap.utils.snap(points, value);
    const from = points.indexOf(gsap.utils.snap(points, settled));
    const reach = points.indexOf(closest);
    let target = closest;
    if (performance.now() - last < TAP_KEEP && Math.abs(reach - from) < 2) {
      target = taps >= TAPS ? points[gsap.utils.clamp(0, points.length - 1, from + way)] : points[from];
    }
    if (target !== points[from]) taps = 0;
    settled = target;
    return target;
  };

  return {
    snap: { snapTo, duration: { min: .3, max: .8 }, delay: .2, ease: 'power2.inOut' },
    onEnter: () => { settled = 0; taps = 0; },
    onEnterBack: () => { settled = 1; taps = 0; },
    kill: () => window.removeEventListener('wheel', wheel)
  };
}

function resetArt({ draw, pop }) {
  gsap.set(draw, { drawSVG: '0%' });
  gsap.set(pop, { scale: 0, autoAlpha: 0, transformOrigin: '50% 50%' });
}

function primeArt(scope, selector = '') {
  const art = {
    draw: $$(`${selector} [data-draw]`.trim(), scope),
    pop: $$(`${selector} [data-pop]`.trim(), scope)
  };
  resetArt(art);
  return art;
}

function drawArt(tl, { draw, pop }, at, { duration, ease, stagger, popAt }) {
  return tl
    .to(draw, { drawSVG: '100%', duration, ease, stagger }, at)
    .to(pop, { scale: 1, autoAlpha: 1, duration: .45, ease: 'back.out(1.6)', stagger: .08 }, popAt);
}

function rise(targets, trigger, start, vars) {
  return gsap.from(targets, {
    y: 24, autoAlpha: 0, duration: .8, ease: 'power3.out', stagger: .08,
    ...vars,
    scrollTrigger: { trigger, start, once: true }
  });
}

function initCurtain(onDone) {
  const curtain = $('#curtain');
  if (!curtain || REDUCED) {
    curtain?.remove();
    onDone();
    return;
  }

  gsap.timeline({ onComplete: () => curtain.remove() })
    .to('.curtain__mark img', { autoAlpha: 1, duration: .5, ease: 'power2.out', startAt: { scale: .8 } })
    .to('.curtain__mark img', { autoAlpha: 0, scale: 1.4, duration: .45, ease: 'power2.in' }, '+=.25')
    .add(onDone)
    .to('.curtain__panel', { yPercent: -101, duration: 1, ease: 'expo.inOut', stagger: .07 }, '<');
}

function initSmoother(mm) {
  mm.add(DESKTOP, () => {
    smoother = ScrollSmoother.create({
      wrapper: '#smooth-wrapper',
      content: '#smooth-content',
      smooth: 1.1,
      effects: true
    });

    const wrapper = $('#smooth-wrapper');
    const forward = () => {
      const dy = wrapper.scrollTop;
      if (!dy && !wrapper.scrollLeft) return;
      wrapper.scrollTop = 0;
      wrapper.scrollLeft = 0;
      if (dy) smoother.scrollTop(smoother.scrollTop() + dy);
    };
    wrapper.addEventListener('scroll', forward, { passive: true });

    return () => {
      wrapper.removeEventListener('scroll', forward);
      if (smoother) { smoother.kill(); smoother = null; }
    };
  });
}

function initPointer(mm) {
  mm.add(`${MOTION} and (hover: hover) and (pointer: fine)`, () => {
    const pointer = $('#pointer');
    if (!pointer) return;
    const tag = $('.pointer__tag', pointer);
    const follow = { duration: .3, ease: 'power3' };
    const [xTo, yTo] = quickXY(pointer, follow);
    const dotScale = gsap.quickTo('.pointer__dot', 'scale', follow);
    const hot = 'a, button, [data-tag]';
    const orange = getComputedStyle(pointer).getPropertyValue('--orange').trim();
    const probe = document.createElement('i');
    probe.style.color = orange;
    document.body.appendChild(probe);
    const orangeRgb = getComputedStyle(probe).color;
    probe.remove();

    const paint = (el) => {
      for (; el && el.nodeType === 1; el = el.parentElement) {
        const bg = getComputedStyle(el).backgroundColor;
        if (bg !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(bg)) {
          pointer.classList.toggle('is-light', bg === orangeRgb);
          return;
        }
      }
      pointer.classList.remove('is-light');
    };

    let seen = false;
    let px = -1;
    let py = -1;
    const move = (e) => {
      if (!seen) { seen = true; gsap.to(pointer, { autoAlpha: 1, duration: .4 }); }
      px = e.clientX;
      py = e.clientY;
      xTo(px);
      yTo(py);
    };
    let queued = false;
    const scroll = () => {
      if (queued || px < 0) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        paint(document.elementFromPoint(px, py));
      });
    };
    const over = (e) => {
      paint(e.target);
      const t = e.target.closest(hot);
      if (!t) return;
      dotScale(2.6);
      if (t.dataset.tag) {
        tag.textContent = t.dataset.tag;
        gsap.to(tag, { autoAlpha: 1, x: 0, duration: .3, startAt: { x: -10 } });
      }
    };
    const out = (e) => {
      if (!e.target.closest(hot)) return;
      dotScale(1);
      gsap.to(tag, { autoAlpha: 0, duration: .2 });
    };

    window.addEventListener('mousemove', move, { passive: true });
    document.addEventListener('mouseover', over);
    document.addEventListener('mouseout', out);
    window.addEventListener('scroll', scroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', scroll);
      window.removeEventListener('mousemove', move);
      document.removeEventListener('mouseover', over);
      document.removeEventListener('mouseout', out);
    };
  });
}

function setTone(tone) {
  document.body.classList.toggle('is-dark', tone === 'dark');
  document.body.classList.toggle('is-orange', tone === 'orange');
}

function initChrome() {
  $$('[data-tone]').forEach((scene) => {
    ScrollTrigger.create({
      trigger: scene,
      start: 'top 50%',
      end: 'bottom 50%',
      refreshPriority: -1,
      onToggle: (self) => { if (self.isActive) setTone(scene.dataset.tone); }
    });
  });
}

function fitOpening() {
  const stage = $('.open__type');
  const avail = stage && stage.clientWidth;
  if (!avail) return;
  const cap = window.innerHeight * .24;
  const rows = $$('.open__row');

  rows.forEach((row) => { row.style.fontSize = ''; });
  rows
    .map((row) => [row, parseFloat(getComputedStyle(row).fontSize), row.scrollWidth])
    .forEach(([row, base, width]) => {
      if (base && width) row.style.fontSize = Math.min(base * (avail / width) * .995, cap).toFixed(2) + 'px';
    });
}

function buildOpening() {
  const rows = $$('.open__row');
  gsap.set('.open__type, .open__row', { visibility: 'visible' });
  fitOpening();

  const tl = gsap.timeline({ paused: true, defaults: { ease: 'expo.out', lazy: false } });
  if (!REDUCED && rows.length) {
    const letters = rows.flatMap((row) => SplitText.create(row, { type: 'words,chars', charsClass: 'ltr' }).chars);
    tl.from(letters, { yPercent: 118, autoAlpha: 0, rotate: 4, duration: 1.2, stagger: .026 }, 0);
  }
  return tl;
}

function initOpen(mm) {
  const hero = $('.open');
  const credo = $('.credo');
  const say = $('.credo__say');
  if (!hero || !credo || !say) return;

  const bare = (self) => document.body.classList.toggle('is-bare', self.scroll() < hero.offsetHeight * .6);
  ScrollTrigger.create({ start: 0, end: 'max', onUpdate: bare, onRefresh: bare });
  document.body.classList.add('is-bare');

  mm.add(MOTION, () => {
    const split = SplitText.create(say, { type: 'words', wordsClass: 'wd' });

    gsap.to('.open__row', {
      scale: 2.4, autoAlpha: 0, ease: 'power2.in', stagger: .15,
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 1.2 }
    });

    gsap.timeline({
      scrollTrigger: { trigger: credo, start: 'top 85%', end: 'top top', scrub: 1.2 }
    })
      .from('.credo__body', { y: 40, autoAlpha: 0, duration: .6, ease: 'power3.out' }, 0)
      .to(split.words, { color: INK, ease: 'none', duration: .5, stagger: .12 }, .3)
      .from('.credo__side > *', { y: 26, autoAlpha: 0, duration: .6, ease: 'power3.out', stagger: .2 }, '>-.1');

    return () => split.revert();
  });
}

function initRound(mm) {
  const round = $('.round');
  if (!round) return;
  const count = $('#roundCount', round);

  mm.add(MOTION, () => {
    const turn = { deg: 0 };
    const write = () => { count.textContent = Math.round(turn.deg); };

    write();

    gsap.timeline({ scrollTrigger: { trigger: round, start: 'top 55%', once: true } })
      .from('.round__say > *, .round__side > :not(.round__dial)', { y: 30, autoAlpha: 0, duration: .8, ease: 'power3.out', stagger: .09 }, 0)
      .to(turn, { deg: 360, duration: 1.8, ease: 'none', onUpdate: write }, .2);

    return () => { count.textContent = '360'; };
  });
}

function initIndex(mm) {
  const cards = $$('.serv');
  if (!cards.length) return;
  const menu = $('#indexMenu');
  const links = $$('.index__link', menu);
  let show = null;

  const light = (i) => {
    const isOn = (_, k) => k === i;
    activate(cards, isOn);
    activate(links, isOn, 'aria-current');
    const link = links[i];
    if (link && menu.scrollWidth > menu.clientWidth) {
      menu.scrollTo({ left: link.offsetLeft - (menu.clientWidth - link.offsetWidth) / 2, behavior: 'smooth' });
    }
  };

  links.forEach((link, i) => link.addEventListener('click', () => {
    if (!show) return scrollToTarget(cards[i]);
    show(i);
    // Sur petit écran, le menu passe sous la fiche : on remonte si elle sort par le haut.
    if (cards[i].getBoundingClientRect().top < 0) scrollToTarget($('.index'));
  }));

  mm.add(MOTION, (ctx) => {
    const arts = cards.map((card) => primeArt(card));
    const texts = cards.map((card) => $$('.serv__name, .serv__desc, .serv__go', card));
    gsap.set(cards, { autoAlpha: 0, yPercent: 0 });
    gsap.set(cards[0], { autoAlpha: 1 });
    gsap.set(texts[0], { y: 26, autoAlpha: 0 });
    let at = 0;

    const paint = (i, delay = 0) => {
      const art = arts[i];
      gsap.killTweensOf([...art.draw, ...art.pop]);
      gsap.killTweensOf(texts[i], 'y,autoAlpha,opacity,visibility'); // épargne l'effet magnétique
      resetArt(art);
      const tl = gsap.timeline({ delay });
      tl.fromTo(texts[i],
        { y: 26, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: .45, ease: 'power3.out', stagger: .05 }, 0);
      drawArt(tl, art, 0, { duration: 1.1, ease: 'power2.inOut', stagger: .07, popAt: '<.9' });
    };

    const intro = ScrollTrigger.create({
      trigger: '.index__pin',
      start: 'top 70%',
      once: true,
      onEnter: () => paint(0)
    });

    ctx.add('show', (i) => {
      if (i === at) return;
      at = i;
      intro.kill();
      light(i);
      gsap.to(cards.filter((card, k) => k !== i),
        { autoAlpha: 0, yPercent: -7, duration: .35, ease: 'power2.in', overwrite: true });
      gsap.fromTo(cards[i],
        { autoAlpha: 0, yPercent: 9 },
        { autoAlpha: 1, yPercent: 0, duration: .45, delay: .12, ease: 'power3.out', overwrite: true });
      paint(i, .17);
    });
    show = ctx.show;

    return () => {
      show = null;
      light(0);
    };
  });
}

function initStack(mm) {
  const slabs = $$('.slab');
  if (!slabs.length) return;

  mm.add(MOTION, () => {
    slabs.forEach((slab, i) => {
      ScrollTrigger.create({
        trigger: slab,
        start: 'top top',
        pin: true,
        pinSpacing: i === slabs.length - 1,
        invalidateOnRefresh: true
      });

      const next = slabs[i + 1];
      if (next) {
        gsap.to(slab, {
          scale: .93, autoAlpha: .45, ease: 'none',
          scrollTrigger: { trigger: next, start: 'top bottom', end: 'top top', scrub: .8 }
        });
      }
    });

    slabs.forEach((slab) => {
      rise($$('.slab__face, .slab__id > *, .slab__art, .slab__txt > *', slab), slab, 'top 60%', { y: 40, stagger: .07 });

      const art = primeArt(slab, '.slab__art');
      if (!art.draw.length && !art.pop.length) return;
      drawArt(
        gsap.timeline({ scrollTrigger: { trigger: slab, start: 'top 55%', once: true } }),
        art, .25, { duration: 1.1, ease: 'power2.out', stagger: .06, popAt: '<.8' }
      );
    });
  });
}

const NAV_KEYS = {
  ArrowDown: 1, PageDown: 1, ' ': 1,
  ArrowUp: -1, PageUp: -1,
  Home: -Infinity, End: Infinity
};
const NAV_QUIET = 120;
const NAV_FRESH = 1.6;

function initSections(mm) {
  mm.add(DESKTOP, () => {
    const marks = ['#agence', '#agence-360', '#services', '#contact']
      .map((id) => $(id))
      .filter(Boolean)
      .map((trigger) => ScrollTrigger.create({ trigger, start: 'top top' }));
    const foot = $('.end');
    const footMark = foot && ScrollTrigger.create({ trigger: foot, start: 'bottom bottom' });

    const stops = () => {
      const max = ScrollTrigger.maxScroll(window);
      const points = [0, ...marks.map((st) => st.start), footMark ? footMark.start : max];
      return [...new Set(points.filter((p) => p != null).map((p) => gsap.utils.clamp(0, max, Math.round(p))))]
        .sort((a, b) => a - b);
    };

    let busy = false;
    let settling = false;
    let lastWheel = 0;
    let lastPush = 0;

    const release = () => {
      const wait = NAV_QUIET - (performance.now() - lastWheel);
      settling = wait > 0;
      if (settling) gsap.delayedCall(wait / 1000, release);
      else busy = false;
    };

    const wheel = (e) => {
      const push = Math.abs(e.deltaY);
      if (settling && push > lastPush * NAV_FRESH + 4) {
        settling = false;
        busy = false;
      }
      lastPush = push;
      lastWheel = performance.now();
    };

    const go = (dir) => {
      if (busy) return;
      const y = currentScroll();
      const points = stops();
      let target;
      if (dir === Infinity) target = points[points.length - 1];
      else if (dir === -Infinity) target = points[0];
      else if (dir > 0) target = points.find((p) => p > y + 2);
      else target = [...points].reverse().find((p) => p < y - 2);
      if (target === undefined || Math.abs(target - y) < 2) return;

      busy = true;
      const screens = Math.abs(target - y) / window.innerHeight;
      tweenScroll(target, {
        duration: gsap.utils.clamp(.6, 1.1, .45 + screens * .25),
        ease: 'power2.inOut',
        overwrite: 'auto',
        onComplete: release,
        onInterrupt: release
      });
    };

    const observer = Observer.create({
      target: window,
      type: 'wheel,touch',
      wheelSpeed: -1,
      tolerance: 4,
      preventDefault: true,
      ignore: '.index__menu, textarea',
      onWheel: (self) => wheel(self.event),
      onUp: () => go(1),
      onDown: () => go(-1)
    });

    const key = (e) => {
      const dir = NAV_KEYS[e.key];
      if (!dir || e.altKey || e.ctrlKey || e.metaKey) return;
      const field = e.target.closest?.('input, textarea, select, [contenteditable]');
      if (field || (e.key === ' ' && e.target.closest?.('a, button'))) return;
      e.preventDefault();
      go(e.shiftKey && e.key === ' ' ? -1 : dir);
    };
    window.addEventListener('keydown', key);

    return () => {
      observer.kill();
      window.removeEventListener('keydown', key);
    };
  });
}

function initSays(mm) {
  const cards = $$('.said');
  if (!cards.length) return;
  const fill = $('#saysFill');

  mm.add({ motion: MOTION, desktop: DESKTOP }, ({ conditions }) => {
    if (!conditions.motion) return;
    gsap.set(cards, { autoAlpha: 0, yPercent: 12 });
    gsap.set(cards[0], { autoAlpha: 1, yPercent: 0 });

    const step = stepper();
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.says',
        start: 'top top',
        end: () => `+=${cards.length * 110}%`,
        pin: true,
        scrub: 1.2,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        snap: conditions.desktop ? step.snap : false,
        onEnter: step.onEnter,
        onEnterBack: step.onEnterBack,
        onUpdate: fill ? (self) => gsap.set(fill, { scaleX: self.progress }) : undefined
      }
    });

    tl.addLabel('said-0');
    cards.slice(1).forEach((card, i) => {
      tl.to(cards[i], { autoAlpha: 0, yPercent: -12, duration: .45, ease: 'power2.in' })
        .fromTo(card,
          { autoAlpha: 0, yPercent: 12 },
          { autoAlpha: 1, yPercent: 0, duration: .55, ease: 'power2.out' }, '<.15')
        .addLabel('said-' + (i + 1))
        .to({}, { duration: .5 });
    });

    return step.kill;
  });
}

function initScenes() {
  rise('.talk__arc, .talk__bar, .recap', '.talk__pin', 'top 65%', { y: 26, stagger: .1 });
  rise('.step.is-on .step__ask, .step.is-on .step__field', '.talk__pin', 'top 65%', { y: 34, duration: .9, stagger: .1 });
  if ($('.end')) rise('.end__mark, .end__claim, .end__reach, .end__foot', '.end', 'top 88%');
}

function initMagnetic(mm) {
  mm.add(DESKTOP, () => {
    const cleanups = $$('.push, .serv__go').map((btn) => {
      // Variables CSS lues par `translate` : n'entre pas en conflit avec les
      // tweens de `transform` qui font apparaître les boutons.
      gsap.set(btn, { '--mx': 0, '--my': 0 });
      const [xTo, yTo] = ['--mx', '--my'].map((prop) =>
        gsap.quickTo(btn, prop, { duration: .5, ease: 'elastic.out(1, .5)' }));
      const move = (e) => {
        const r = btn.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * .32);
        yTo((e.clientY - (r.top + r.height / 2)) * .32);
      };
      const leave = () => { xTo(0); yTo(0); };
      btn.addEventListener('mousemove', move);
      btn.addEventListener('mouseleave', leave);
      return () => {
        btn.removeEventListener('mousemove', move);
        btn.removeEventListener('mouseleave', leave);
        gsap.set(btn, { clearProps: '--mx,--my' });
      };
    });
    return () => cleanups.forEach((fn) => fn());
  });
}

// Sur mobile, le clavier qui s'ouvre réduit la hauteur de la fenêtre : ScrollTrigger
// recalculerait toute la page (dont la taille du titre d'accueil) et ferait sauter le
// scroll sous le doigt. On ignore donc les redimensionnements tant qu'un champ a le focus.
const REFRESH_EVENTS = 'visibilitychange,DOMContentLoaded,load';
const FIELD = 'input, textarea, select, [contenteditable]';

function holdRefreshWhileTyping() {
  if (!ScrollTrigger.isTouch) return;
  document.addEventListener('focusin', (e) => {
    if (e.target.closest?.(FIELD)) ScrollTrigger.config({ autoRefreshEvents: REFRESH_EVENTS });
  });
  document.addEventListener('focusout', (e) => {
    if (e.target.closest?.(FIELD)) ScrollTrigger.config({ autoRefreshEvents: REFRESH_EVENTS + ',resize' });
  });
}

function initJumps() {
  const initial = window.location.hash && document.querySelector(window.location.hash);
  if (initial) {
    window.addEventListener('load', () => {
      ScrollTrigger.refresh();
      gsap.delayedCall(.1, () => scrollToTarget(initial));
    });
  }
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"][data-jump]');
    const target = link && document.querySelector(link.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    scrollToTarget(target);
  });
}

export function initAnimations() {
  document.documentElement.classList.add('js-ready');
  gsap.defaults({ ease: 'power3.out', duration: .9 });

  const mm = gsap.matchMedia();

  initSmoother(mm);
  const opening = buildOpening();

  initChrome();
  initPointer(mm);
  initMagnetic(mm);
  initOpen(mm);
  initRound(mm);
  initIndex(mm);
  initStack(mm);
  initSays(mm);
  initSections(mm);
  initScenes();
  initJumps();
  holdRefreshWhileTyping();

  initCurtain(() => opening.play());

  ScrollTrigger.addEventListener('refreshInit', fitOpening);
  document.fonts?.ready.then(() => ScrollTrigger.refresh());

  return {
    reduced: REDUCED,

    jumpTo: scrollToTarget,

    arc(node, ratio) {
      gsap.to(node, { scaleX: gsap.utils.clamp(.06, 1, ratio), duration: .6, ease: 'power3.out' });
    },

    swapStep(from, to, dir) {
      if (REDUCED) return;
      const way = dir < 0 ? -1 : 1;
      gsap.timeline()
        .to(from, {
          yPercent: -18 * way, autoAlpha: 0, duration: .35, ease: 'power2.in',
          onComplete: () => from.classList.remove('is-on')
        })
        .set(to, { yPercent: 18 * way })
        .add(() => to.classList.add('is-on'))
        .to(to, { yPercent: 0, autoAlpha: 1, duration: .5, ease: 'power3.out', startAt: { autoAlpha: 0 } })
        .from($$('.step__ask, .step__field', to), {
          y: 22, autoAlpha: 0, duration: .5, stagger: .07, ease: 'power3.out'
        }, '<.05');
    },

    shake(node) {
      if (REDUCED) return;
      gsap.fromTo(node, { x: -9 }, { x: 0, duration: .5, ease: 'elastic.out(1.6, .35)', overwrite: true });
    },

    reveal(node) {
      gsap.fromTo(node, { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: .5, overwrite: true });
    },

    celebrate() {
      if (REDUCED) return;
      const dot = document.createElement('span');
      dot.className = 'burst';
      document.body.appendChild(dot);
      gsap.timeline({ onComplete: () => dot.remove() })
        .to(dot, { scale: 90, duration: .85, ease: 'power2.in' })
        .to(dot, { autoAlpha: 0, duration: .5, ease: 'power2.out' }, '-=.2');
    }
  };
}
